import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faTimesCircle, faShieldAlt, faFileContract, faExclamationTriangle, faArrowRight, faCheckCircle, faCircle } from '@/fontawesome';
import { Link } from 'react-router-dom';
import { useState } from 'react';
import PasswordInput from '@/components/forms/PasswordInput';
import LegalDocumentModal from '@/components/auth/LegalDocumentModal';
import SelectedPlansSummary from '@/components/checkout/SelectedPlansSummary';
import { datenschutzContent } from '@/content/legal/datenschutz';
import { agbContent } from '@/content/legal/agb';
import { risikoDisclaimerContent } from '@/content/legal/risikodisclaimer';
import { validatePhoneE164 } from '@/utils/helpers';
import type { SubscriptionType } from '@/models';

interface StepAccountDetailsProps {
  selectedPlans: SubscriptionType[];
  /** Ein Gutschein aus dem Link wird im Checkout angewendet (beeinflusst nur die Preis-Anzeige). */
  hasCoupon: boolean;
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  password: string;
  passwordConfirm: string;
  acceptedDatenschutz: boolean;
  acceptedAGB: boolean;
  acceptedDisclaimer: boolean;
  error: string;
  loading: boolean;
  showLoginLink: boolean;
  onEmailChange: (value: string) => void;
  onFirstNameChange: (value: string) => void;
  onLastNameChange: (value: string) => void;
  onPhoneChange: (value: string) => void;
  onPasswordChange: (value: string) => void;
  onPasswordConfirmChange: (value: string) => void;
  onAcceptedDatenschutzChange: (value: boolean) => void;
  onAcceptedAGBChange: (value: boolean) => void;
  onAcceptedDisclaimerChange: (value: boolean) => void;
  onNext: () => void;
}

const INPUT_CLASS = "w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors";

function isEmailValid(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email.trim());
}

interface PasswordCheck {
  label: string;
  met: boolean;
}

function getPasswordChecks(password: string): PasswordCheck[] {
  return [
    { label: 'Mindestens 8 Zeichen', met: password.length >= 8 },
    { label: 'Mindestens ein Großbuchstabe', met: /[A-Z]/.test(password) },
    { label: 'Mindestens ein Kleinbuchstabe', met: /[a-z]/.test(password) },
    { label: 'Mindestens eine Zahl', met: /[0-9]/.test(password) },
    { label: 'Mindestens ein Sonderzeichen', met: /[^A-Za-z0-9]/.test(password) },
  ];
}

function isPasswordValid(password: string): boolean {
  return getPasswordChecks(password).every(c => c.met);
}

interface ValidationIssue {
  message: string;
}

function getValidationIssues(props: StepAccountDetailsProps): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  if (props.email.trim() === '') {
    issues.push({ message: 'E-Mail-Adresse eingeben' });
  } else if (!isEmailValid(props.email)) {
    issues.push({ message: 'Gültige E-Mail-Adresse eingeben' });
  }
  if (props.firstName.trim() === '') {
    issues.push({ message: 'Vorname eingeben' });
  }
  if (props.lastName.trim() === '') {
    issues.push({ message: 'Nachname eingeben' });
  }
  if (props.phone.trim() !== '' && !validatePhoneE164(props.phone).valid) {
    issues.push({ message: 'Telefonnummer muss mit Ländervorwahl beginnen (z. B. +491701234567)' });
  }
  if (!isPasswordValid(props.password)) {
    issues.push({ message: 'Passwort erfüllt nicht alle Anforderungen' });
  }
  if (props.password !== props.passwordConfirm) {
    issues.push({ message: 'Passwörter stimmen nicht überein' });
  }
  if (!props.acceptedDatenschutz || !props.acceptedAGB || !props.acceptedDisclaimer) {
    issues.push({ message: 'Rechtliche Dokumente akzeptieren' });
  }
  return issues;
}

function isFormValid(props: StepAccountDetailsProps): boolean {
  return getValidationIssues(props).length === 0;
}

export default function StepAccountDetails(props: StepAccountDetailsProps) {
  const {
    selectedPlans, hasCoupon,
    email, firstName, lastName, phone, password, passwordConfirm,
    acceptedDatenschutz, acceptedAGB, acceptedDisclaimer,
    error, loading, showLoginLink,
    onEmailChange, onFirstNameChange, onLastNameChange, onPhoneChange,
    onPasswordChange, onPasswordConfirmChange,
    onAcceptedDatenschutzChange, onAcceptedAGBChange, onAcceptedDisclaimerChange,
    onNext,
  } = props;

  const [isDatenschutzModalOpen, setIsDatenschutzModalOpen] = useState(false);
  const [isAGBModalOpen, setIsAGBModalOpen] = useState(false);
  const [isDisclaimerModalOpen, setIsDisclaimerModalOpen] = useState(false);
  const [showValidationHints, setShowValidationHints] = useState(false);
  const [phoneTouched, setPhoneTouched] = useState(false);

  const valid = isFormValid(props);
  const validationIssues = getValidationIssues(props);
  const passwordChecks = getPasswordChecks(password);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!valid) {
      setShowValidationHints(true);
      return;
    }
    if (!loading) {
      onNext();
    }
  };

  return (
    <>
      <form className="space-y-4" onSubmit={handleSubmit}>
        {/* Selected plan(s) */}
        <SelectedPlansSummary plans={selectedPlans} withPrefix couponApplied={hasCoupon} />

        {/* Email */}
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            E-Mail <span className="text-red-500">*</span>
          </label>
          <input
            id="email"
            type="email"
            required
            value={email}
            onChange={(e) => onEmailChange(e.target.value)}
            className={INPUT_CLASS}
            placeholder="max@example.com"
            autoComplete="username"
          />
        </div>

        {/* Name fields */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Vorname <span className="text-red-500">*</span>
            </label>
            <input
              id="firstName"
              type="text"
              required
              value={firstName}
              onChange={(e) => onFirstNameChange(e.target.value)}
              className={INPUT_CLASS}
              placeholder="Max"
              autoComplete="given-name"
            />
          </div>
          <div>
            <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Nachname <span className="text-red-500">*</span>
            </label>
            <input
              id="lastName"
              type="text"
              required
              value={lastName}
              onChange={(e) => onLastNameChange(e.target.value)}
              className={INPUT_CLASS}
              placeholder="Mustermann"
              autoComplete="family-name"
            />
          </div>
        </div>

        {/* Phone */}
        <div>
          <label htmlFor="phone" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Telefon <span className="text-gray-500 dark:text-gray-400 font-normal">(optional)</span>
          </label>
          <input
            id="phone"
            type="tel"
            value={phone}
            onChange={(e) => onPhoneChange(e.target.value)}
            onBlur={() => setPhoneTouched(true)}
            className={`${INPUT_CLASS} ${phoneTouched && phone.trim() !== '' && !validatePhoneE164(phone).valid ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : ''}`}
            placeholder="+491701234567"
            autoComplete="tel"
          />
          {phoneTouched && (
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Mit Ländervorwahl, z. B. +491701234567
            </p>
          )}
          {phoneTouched && phone.trim() !== '' && !validatePhoneE164(phone).valid && (
            <p className="mt-1 text-xs text-red-500">
              Telefonnummer muss mit Ländervorwahl beginnen (z. B. +491701234567)
            </p>
          )}
        </div>

        {/* Password */}
        <PasswordInput
          id="password"
          name="new-password"
          value={password}
          onChange={(e) => onPasswordChange(e.target.value)}
          placeholder="Sicheres Passwort wählen"
          label={<>Passwort <span className="text-red-500">*</span></>}
          required
          autoComplete="new-password"
        />

        {/* Password requirements checklist */}
        {password.length > 0 && (
          <div className="space-y-1 -mt-2">
            {passwordChecks.map((check) => (
              <div key={check.label} className="flex items-center gap-2">
                <FontAwesomeIcon
                  icon={check.met ? faCheckCircle : faCircle}
                  className={`w-3 h-3 ${check.met ? 'text-green-500' : 'text-gray-300 dark:text-gray-600'}`}
                />
                <span className={`text-xs ${check.met ? 'text-green-600 dark:text-green-400' : 'text-gray-500 dark:text-gray-400'}`}>
                  {check.label}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Password confirm */}
        <PasswordInput
          id="passwordConfirm"
          name="confirm-password"
          value={passwordConfirm}
          onChange={(e) => onPasswordConfirmChange(e.target.value)}
          placeholder="Passwort wiederholen"
          label={<>Passwort bestätigen <span className="text-red-500">*</span></>}
          required
          autoComplete="new-password"
        />

        {/* Password mismatch hint */}
        {passwordConfirm.length > 0 && password !== passwordConfirm && (
          <p className="text-xs text-red-500 -mt-2">Die Passwörter stimmen nicht überein</p>
        )}

        {/* Error display */}
        {error !== '' && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <FontAwesomeIcon icon={faTimesCircle} className="h-5 w-5 text-red-400" />
              </div>
              <div className="ml-3">
                <p className="text-red-700 dark:text-red-400 text-sm font-medium">{error}</p>
                {showLoginLink && (
                  <Link
                    to="/login"
                    className="text-blue-600 dark:text-blue-400 hover:text-blue-500 dark:hover:text-blue-300 text-sm font-medium underline mt-1 inline-block"
                  >
                    Jetzt anmelden
                  </Link>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Legal documents */}
        <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-4 space-y-3 border border-gray-200 dark:border-gray-700">
          <p className="text-sm font-medium text-gray-900 dark:text-white mb-3">
            Rechtliche Dokumente
          </p>

          {/* Datenschutzerklärung */}
          <label
            className="flex items-start gap-3 cursor-pointer"
            onClick={(e) => {
              if (!acceptedDatenschutz) {
                e.preventDefault();
                setIsDatenschutzModalOpen(true);
              }
            }}
          >
            <input
              type="checkbox"
              checked={acceptedDatenschutz}
              onChange={(e) => {
                if (acceptedDatenschutz) {
                  onAcceptedDatenschutzChange(e.target.checked);
                } else {
                  setIsDatenschutzModalOpen(true);
                }
              }}
              className="mt-0.5 h-5 w-5 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 cursor-pointer"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300 flex-1">
              Ich habe die{' '}
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setIsDatenschutzModalOpen(true); }}
                className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 underline font-medium cursor-pointer"
              >
                Datenschutzerklärung
              </button>
              {' '}gelesen und akzeptiere sie.
            </span>
          </label>

          {/* AGB */}
          <label
            className="flex items-start gap-3 cursor-pointer"
            onClick={(e) => {
              if (!acceptedAGB) {
                e.preventDefault();
                setIsAGBModalOpen(true);
              }
            }}
          >
            <input
              type="checkbox"
              checked={acceptedAGB}
              onChange={(e) => {
                if (acceptedAGB) {
                  onAcceptedAGBChange(e.target.checked);
                } else {
                  setIsAGBModalOpen(true);
                }
              }}
              className="mt-0.5 h-5 w-5 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 cursor-pointer"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300 flex-1">
              Ich habe die{' '}
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setIsAGBModalOpen(true); }}
                className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 underline font-medium cursor-pointer"
              >
                AGB
              </button>
              {' '}gelesen und akzeptiere sie.
            </span>
          </label>

          {/* Risikohinweis */}
          <label
            className="flex items-start gap-3 cursor-pointer"
            onClick={(e) => {
              if (!acceptedDisclaimer) {
                e.preventDefault();
                setIsDisclaimerModalOpen(true);
              }
            }}
          >
            <input
              type="checkbox"
              checked={acceptedDisclaimer}
              onChange={(e) => {
                if (acceptedDisclaimer) {
                  onAcceptedDisclaimerChange(e.target.checked);
                } else {
                  setIsDisclaimerModalOpen(true);
                }
              }}
              className="mt-0.5 h-5 w-5 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 cursor-pointer"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300 flex-1">
              Ich habe den{' '}
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setIsDisclaimerModalOpen(true); }}
                className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 underline font-medium cursor-pointer"
              >
                Risikohinweis
              </button>
              {' '}gelesen und akzeptiere ihn.
            </span>
          </label>
        </div>

        {/* Validation hints (shown after first attempt with invalid form) */}
        {showValidationHints && validationIssues.length > 0 && (
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
            <p className="text-sm font-medium text-amber-800 dark:text-amber-300 mb-2">Bitte korrigiere folgende Punkte:</p>
            <ul className="space-y-1">
              {validationIssues.map((issue) => (
                <li key={issue.message} className="text-sm text-amber-700 dark:text-amber-400 flex items-center gap-2">
                  <span className="w-1 h-1 bg-amber-500 rounded-full shrink-0" />
                  {issue.message}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Submit button */}
        <button
          type="submit"
          disabled={loading}
          className={`w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${
            valid
              ? 'bg-blue-600 hover:bg-blue-700 cursor-pointer'
              : 'bg-blue-600/60 cursor-pointer'
          }`}
        >
          {loading ? (
            <>
              <FontAwesomeIcon icon={faSpinner} className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" />
              Code wird angefordert...
            </>
          ) : (
            <>
              Weiter
              <FontAwesomeIcon icon={faArrowRight} className="ml-2 h-4 w-4" />
            </>
          )}
        </button>

        {/* Back link */}
        <div className="text-center">
          <Link
            to="/#preise"
            className="inline-flex items-center text-sm text-blue-600 dark:text-blue-400 hover:text-blue-500 dark:hover:text-blue-300 font-medium"
          >
            Paket ändern
          </Link>
        </div>
      </form>

      {/* Legal Document Modals */}
      <LegalDocumentModal
        isOpen={isDatenschutzModalOpen}
        onClose={() => setIsDatenschutzModalOpen(false)}
        onAccept={() => onAcceptedDatenschutzChange(true)}
        content={datenschutzContent}
        icon={<FontAwesomeIcon icon={faShieldAlt} className="w-5 h-5 text-blue-600 dark:text-blue-400" />}
      />
      <LegalDocumentModal
        isOpen={isAGBModalOpen}
        onClose={() => setIsAGBModalOpen(false)}
        onAccept={() => onAcceptedAGBChange(true)}
        content={agbContent}
        icon={<FontAwesomeIcon icon={faFileContract} className="w-5 h-5 text-blue-600 dark:text-blue-400" />}
      />
      <LegalDocumentModal
        isOpen={isDisclaimerModalOpen}
        onClose={() => setIsDisclaimerModalOpen(false)}
        onAccept={() => onAcceptedDisclaimerChange(true)}
        content={risikoDisclaimerContent}
        icon={<FontAwesomeIcon icon={faExclamationTriangle} className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />}
      />
    </>
  );
}
